// src/lib/numberFormatting.ts
/**
 * Phase 3: Enhanced Number Formatting
 * Provides formatting functions for all number types including currency and custom units
 */

import { formatNumber, formatNumberWithPrecision, LocaleCode, getLocaleConfig } from './locale';
import { formatFractionForMath, decimalToFraction, decimalToUnsimplifiedFraction } from './secondaryLineFormatting';
import type { NumberFormatConfig } from '../types/redux';

/**
 * Format a number with exact decimal places (Phase 3)
 * Unlike formatNumberWithPrecision which determines places automatically,
 * this function uses exactly the specified number of decimal places
 */
function formatNumberWithExactPlaces(
  value: number,
  locale: LocaleCode,
  decimalPlaces: number
): string {
  if (!isFinite(value)) {
    return value.toString();
  }

  const formatted = value.toFixed(decimalPlaces);
  const config = getLocaleConfig(locale);
  
  if (config.decimalSeparator === ',') {
    return formatted.replace('.', ',');
  }
  
  return formatted;
}

/**
 * Format a number based on NumberFormatConfig
 * 
 * @param value - The numeric value to format
 * @param config - Number format configuration
 * @param locale - User's locale for number formatting
 * @returns Formatted string (LaTeX for fractions, plain text otherwise)
 */
export function formatNumberWithConfig(
  value: number,
  config: NumberFormatConfig,
  locale: LocaleCode
): string {
  switch (config.type) {
    case 'integer':
      return formatNumber(Math.round(value), locale);
    
    case 'decimal': {
      const places = config.decimalPlaces ?? 'auto';
      if (places === 'auto') {
        return value.toLocaleString(locale, { 
          maximumFractionDigits: 6,
          minimumFractionDigits: 0 
        });
      }
      return formatNumberWithExactPlaces(value, locale, places);
    }
    
    case 'fraction': {
      const fraction = config.fractionSimplify !== false
        ? decimalToFraction(value)
        : decimalToUnsimplifiedFraction(value);
      
      return formatFractionForMath(fraction, {
        format: config.fractionFormat || 'improper',
        simplify: config.fractionSimplify !== false,
      });
    }
    
    case 'percentage': {
      const percentValue = value * 100;
      const places = config.decimalPlaces ?? 1;
      let formatted;
      if (places === 'auto') {
        formatted = percentValue.toLocaleString(locale, { 
          maximumFractionDigits: 6,
          minimumFractionDigits: 0 
        });
      } else {
        formatted = formatNumberWithExactPlaces(percentValue, locale, places);
      }
      return `${formatted}%`;
    }
    
    case 'currency': {
      // Currency always uses 2 decimal places (standard practice)
      const formatted = formatNumberWithExactPlaces(value, locale, 2);
      const symbol = config.currencySymbol || '£';
      
      if (config.currencyPosition === 'after') {
        return `${formatted}${symbol}`;
      }
      return `${symbol}${formatted}`;
    }
    
    case 'custom': {
      const places = config.decimalPlaces ?? 2;
      let formatted;
      if (places === 'auto') {
        formatted = value.toLocaleString(locale, { 
          maximumFractionDigits: 6,
          minimumFractionDigits: 0 
        });
      } else {
        formatted = formatNumberWithExactPlaces(value, locale, places);
      }
      const unit = config.customUnit || '';
      
      if (!unit) {
        return formatted;
      }
      
      if (config.unitPosition === 'before') {
        return `${unit}${formatted}`;
      }
      return `${formatted}${unit}`;
    }
    
    default:
      return formatNumber(value, locale);
  }
}

/**
 * Apply unit suffix to a formatted number string
 * Useful for when the base number is already formatted but unit needs to be added
 * 
 * @param formatted - Already formatted number string
 * @param config - Number format configuration (only custom unit types apply)
 * @returns String with unit suffix applied
 */
export function applyUnitSuffix(
  formatted: string,
  config: NumberFormatConfig
): string {
  if (config.type !== 'custom' || !config.customUnit) {
    return formatted;
  }
  
  const unit = config.customUnit;
  
  if (config.unitPosition === 'before') {
    return `${unit}${formatted}`;
  }
  return `${formatted}${unit}`;
}

/**
 * Get display name for a number format type
 * Used in UI dropdowns
 */
export function getFormatTypeLabel(type: NumberFormatConfig['type']): string {
  switch (type) {
    case 'integer': return 'Integer';
    case 'decimal': return 'Decimal';
    case 'fraction': return 'Fraction';
    case 'percentage': return 'Percentage';
    case 'currency': return 'Currency';
    case 'custom': return 'Custom Unit';
    default: return 'Unknown';
  }
}

/**
 * Validate number format configuration
 * Returns validation errors if any
 */
export function validateNumberFormat(config: NumberFormatConfig): string[] {
  const errors: string[] = [];
  
  if (config.type === 'decimal' || config.type === 'currency') {
    if (config.decimalPlaces !== undefined && config.decimalPlaces !== 'auto' && (config.decimalPlaces < 0 || config.decimalPlaces > 3)) {
      errors.push('Decimal places must be between 0 and 3');
    }
  }
  
  if (config.type === 'currency' && !config.currencySymbol) {
    errors.push('Currency symbol is required for currency format');
  }
  
  if (config.type === 'custom' && !config.customUnit) {
    errors.push('Custom unit is required for custom format');
  }
  
  return errors;
}

/**
 * Currency symbols commonly used
 */
export const CURRENCY_SYMBOLS = [
  { symbol: '£', label: 'British Pound (£)', code: 'GBP' },
  { symbol: '$', label: 'US Dollar ($)', code: 'USD' },
  { symbol: '€', label: 'Euro (€)', code: 'EUR' },
  { symbol: '¥', label: 'Japanese Yen (¥)', code: 'JPY' },
  { symbol: '₹', label: 'Indian Rupee (₹)', code: 'INR' },
  { symbol: 'kr', label: 'Swedish Krona (kr)', code: 'SEK' },
] as const;

/**
 * Common unit suffixes for custom formatting
 */
export const COMMON_UNITS = [
  { unit: 'g', label: 'Grams (g)', category: 'weight' },
  { unit: 'kg', label: 'Kilograms (kg)', category: 'weight' },
  { unit: 'ml', label: 'Milliliters (ml)', category: 'volume' },
  { unit: 'L', label: 'Liters (L)', category: 'volume' },
  { unit: 'cm', label: 'Centimeters (cm)', category: 'length' },
  { unit: 'm', label: 'Meters (m)', category: 'length' },
  { unit: 'km', label: 'Kilometers (km)', category: 'length' },
  { unit: '°C', label: 'Celsius (°C)', category: 'temperature' },
  { unit: '°F', label: 'Fahrenheit (°F)', category: 'temperature' },
  { unit: 's', label: 'Seconds (s)', category: 'time' },
  { unit: 'min', label: 'Minutes (min)', category: 'time' },
  { unit: 'h', label: 'Hours (h)', category: 'time' },
] as const;

/**
 * Format a number for display at extreme zoom levels
 * 
 * Handles:
 * - Large numbers when zoomed out (abbreviations: K, M)
 * - Small decimals when zoomed in (scientific notation)
 * - Standard numbers at normal zoom levels
 * 
 * @param value - The numeric value to format
 * @param scale - Current zoom scale (px/unit)
 * @param locale - Locale for number formatting
 * @returns Formatted string for display
 */
export function formatExtremeValue(
  value: number,
  scale: number,
  locale: string = 'en-GB'
): string {
  const absValue = Math.abs(value);
  
  // Handle zero
  if (value === 0) return '0';
  
  // Large numbers when zoomed out (scale < 1)
  if (scale < 1 && absValue >= 1000) {
    if (absValue >= 1_000_000) {
      const formatted = (value / 1_000_000);
      return `${formatted.toLocaleString(locale, { maximumFractionDigits: 1 })}M`;
    }
    if (absValue >= 1_000) {
      const formatted = (value / 1_000);
      return `${formatted.toLocaleString(locale, { maximumFractionDigits: 1 })}K`;
    }
  }
  
  // Very small decimals when zoomed in (scale > 1000)
  if (scale > 1000 && absValue < 0.001 && absValue > 0) {
    return value.toExponential(2);
  }
  
  // Standard formatting with appropriate precision
  if (absValue >= 1000) {
    return Math.round(value).toLocaleString(locale);
  }
  
  if (absValue >= 1) {
    return value.toLocaleString(locale, { maximumFractionDigits: 4 });
  }
  
  if (absValue >= 0.001) {
    return value.toLocaleString(locale, { maximumFractionDigits: 6 });
  }
  
  // Very small values
  return value.toExponential(3);
}

/**
 * Determine appropriate decimal places based on zoom level
 * 
 * @param scale - Current zoom scale
 * @returns Number of decimal places to show
 */
export function getDecimalPlacesForZoom(scale: number): number {
  if (scale < 1) return 0;
  if (scale < 10) return 1;
  if (scale < 100) return 2;
  if (scale < 1000) return 3;
  if (scale < 5000) return 4;
  return 5;
}

